// features/inputs/services/inputBatchesApi.ts
import { apiClient } from '@/shared/services/api';
import type { ApiResponse } from '@/features/auth/services/authApi';

export interface InputBatch {
  id: string;
  inputId: string;
  inputName: string;
  inputCode: string;
  batchNumber: string;
  manufacturerBatchNumber?: string;
  lotNumber?: string;
  receivedQuantity: number;
  currentQuantity: number;
  reservedQuantity: number;
  unitOfMeasure: string;
  expiryDate?: string;
  manufactureDate?: string;
  receivedDate: string;
  supplierId?: string;
  supplierName?: string;
  unitCost: number;
  vatPercentage?: number;
  totalCost: number;
  purchaseOrderNumber?: string;
  invoiceNumber?: string;
  isExpired: boolean;
  isAvailable: boolean;
  isActive: boolean;
  approvalStatus: string;
  createdAt: string;
}

export interface CreateInputBatchRequest {
  inputId: string;
  batchNumber: string;
  quantity: number;
  expiryDate?: string;
  receivedDate: string;
  supplierId: string;
  supplierBatchNumber?: string;
  unitCost: number;
  storageLocation?: string;
}

export const inputBatchesApi = {
  getBatchesByInput: async (inputId: string): Promise<InputBatch[]> => {
    const { data } = await apiClient.get<ApiResponse<InputBatch[]>>(
      `/stock/batches/input/${inputId}`
    );
    return data.data;
  },

  getBatchById: async (id: string): Promise<InputBatch> => {
    const { data } = await apiClient.get<ApiResponse<InputBatch>>(
      `/stock/batches/${id}`
    );
    return data.data;
  },

  getActiveBatches: async (): Promise<InputBatch[]> => {
    const { data } = await apiClient.get<ApiResponse<InputBatch[]>>(
      '/stock/batches/active'
    );
    return data.data;
  },

  getExpiringBatches: async (days: number): Promise<InputBatch[]> => {
    const { data } = await apiClient.get<ApiResponse<InputBatch[]>>(
      `/stock/batches/expiring?daysAhead=${days}`
    );
    return data.data;
  },

  createBatch: async (
    payload: CreateInputBatchRequest
  ): Promise<InputBatch> => {
    const { data } = await apiClient.post<ApiResponse<InputBatch>>(
      '/InputBatches',
      payload
    );
    return data.data;
  },

  updateBatch: async (
    id: string,
    payload: Partial<CreateInputBatchRequest>
  ): Promise<InputBatch> => {
    const { data } = await apiClient.put<ApiResponse<InputBatch>>(
      `/InputBatches/${id}`,
      payload
    );
    return data.data;
  },

  deleteBatch: async (id: string): Promise<void> => {
    await apiClient.delete(`/InputBatches/${id}`);
  },
};